// The 1-2-1's reduced editor (stage4-for-adam 14; the map's recommendation): a client's session holds
// only its recording's Vimeo link, its length (read from Vimeo, or typed) and Adam's notes, in
// client_session_notes. "Save and show Laura" shows them to the client at once, as the client space's
// own "After session" box does, with the same words. Owner and admin only: support and hosts never
// read a client space. Its foot is pinned to the bottom of the screen as the recording editor's is, and
// it tells the list through onDirty whether anything typed isn't saved yet, and through onSaving whether
// a save is going on (Adam, 13 Oct).
import { useEffect, useId, useState } from 'react'
import { Button, Field, TextArea, TextInput } from '../ui/index.js'
import { COPY as SPACE } from '../../products/mentorship/copy.js'
import { COPY } from './copy.js'
import { StateChip, LinkNote } from './Head.jsx'
import { firstName, headingFor, parseLength } from './model.js'
import { useVimeoRead } from './vimeoRead.js'
import { saveClientNotes } from './data.js'

export function ClientEditor({ item, api, onSaved, onDirty, onSaving, flash, clearFlash }) {
  const { note, clientSession, client } = item
  const first = firstName(client) || client?.full_name || ''
  const shown = Boolean(note?.shown_at)
  const [link, setLink] = useState(note?.recording_url || '')
  const [notes, setNotes] = useState(note?.notes || '')
  const [lengthTyped, setLengthTyped] = useState('')
  const [needLength, setNeedLength] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  const ids = useId()
  const vimeo = useVimeoRead(link, { savedUrl: note?.recording_url, savedSeconds: note?.duration_seconds })
  const showLength = needLength || vimeo.state === 'failed' || (lengthTyped !== '' && vimeo.state !== 'known')
  const touch = setter => v => { setter(v); setProblem(null); clearFlash?.() }
  const dirty = link !== (note?.recording_url || '') || notes !== (note?.notes || '') || lengthTyped !== ''
  useEffect(() => { onDirty?.(dirty) }, [dirty, onDirty])
  useEffect(() => () => onDirty?.(false), [onDirty])
  useEffect(() => { onSaving?.(busy) }, [busy, onSaving])
  useEffect(() => () => onSaving?.(false), [onSaving])

  async function save() {
    if (busy) return
    setProblem(null)
    clearFlash?.()
    if (vimeo.state === 'empty') return setProblem(SPACE.afterNeedLink)
    if (vimeo.state === 'bad') return setProblem(SPACE.afterBadLink)
    if (lengthTyped.trim() && !parseLength(lengthTyped)) return setProblem(COPY.lengthBad)
    setBusy(true)
    try {
      let seconds = vimeo.seconds
      if (!seconds) seconds = (await vimeo.settled())?.seconds || parseLength(lengthTyped)
      if (!seconds && vimeo.state !== 'known') {
        // the recording still plays without its length, but it can't resume or tick at 90% without it
        setNeedLength(true)
        if (!lengthTyped.trim()) { setProblem(COPY.needLength); return }
      }
      await saveClientNotes(api, { session: clientSession, note, url: vimeo.url, seconds, notes })
      await onSaved(clientSession.id, shown ? COPY.clientSavedChange(first) : SPACE.afterSaved(first, clientSession.number))
    } catch (e) {
      setProblem(api.words(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="ad-card adc-editor" aria-labelledby={`${ids}-h`}>
      <div className="adc-editor-head">
        <div className="adc-editor-title">
          <span className="adc-eyebrow">{COPY.clientGroup(item.product?.name || '', client?.full_name || '')}</span>
          <h2 className="adc-h2" id={`${ids}-h`} tabIndex={-1}>{headingFor(item)}</h2>
        </div>
        <StateChip chip={item.chip} big />
      </div>
      <Field label={COPY.link} plain>
        {props => (
          <TextInput {...props} value={link} onChange={touch(setLink)} placeholder={COPY.linkPlaceholder}
            aria-describedby={`${ids}-note`} autoComplete="off" spellCheck={false} />
        )}
      </Field>
      <LinkNote link={vimeo} id={`${ids}-note`} />
      {showLength && (
        <Field label={COPY.length} hint={COPY.lengthHint} plain className="adc-length">
          {props => <TextInput {...props} value={lengthTyped} onChange={touch(setLengthTyped)} placeholder={COPY.lengthPlaceholder} inputMode="numeric" />}
        </Field>
      )}
      <Field label={COPY.notes} plain>
        {props => <TextArea {...props} value={notes} onChange={touch(setNotes)} placeholder={SPACE.afterNotesPlaceholder(first)} rows={6} />}
      </Field>
      <div className="adc-dock">
        <div className="adc-foot adc-foot--end">
          <Button kind="ink" busy={busy} onClick={save}>{shown ? COPY.saveChanges : SPACE.afterSave(first)}</Button>
        </div>
        {problem && <p className="ad-error" role="alert">{problem}</p>}
        {flash && <p className="adc-flash" role="status">{flash}</p>}
      </div>
    </section>
  )
}
